import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Chip,
    TextField,
    MenuItem,
    IconButton,
    Tooltip,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Avatar,
    Stack,
    Button,
    CircularProgress,
    Tabs,
    Tab,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    FilterList as FilterListIcon,
    Search as SearchIcon,
    Launch as LaunchIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import toast from 'react-hot-toast';
import { emailsAPI, adminFormsAPI } from '../../services/api';

dayjs.extend(relativeTime);

const statusColor = (status) => {
    switch (status) {
        case 'processed':
            return 'success';
        case 'pending':
            return 'warning';
        case 'failed':
        case 'spam':
            return 'error';
        default:
            return 'default';
    }
};

const SuperAdminFormsPage = () => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all'); // all | byWebsite
    const [submissions, setSubmissions] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
    const [limit, setLimit] = useState(10);

    // Websites tab state
    const [websitesLoading, setWebsitesLoading] = useState(false);
    const [websites, setWebsites] = useState([]);
    const [webPagination, setWebPagination] = useState({ currentPage: 1, totalPages: 1, totalWebsites: 0 });
    const [webLimit, setWebLimit] = useState(10);

    const [filters, setFilters] = useState({
        q: '',
        status: '',
        dateRange: '', // today | thisWeek | thisMonth | lastMonth
    });

    const [analytics, setAnalytics] = useState(null);

    const computeDateRange = (key) => {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const startOfPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const startOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

        switch (key) {
            case 'today':
                return { startDate: startOfToday.toISOString(), endDate: undefined };
            case 'thisWeek':
                return { startDate: startOfWeek.toISOString(), endDate: undefined };
            case 'thisMonth':
                return { startDate: startOfMonth.toISOString(), endDate: undefined };
            case 'lastMonth':
                return { startDate: startOfPrevMonth.toISOString(), endDate: startOfMonth.toISOString() };
            default:
                return { startDate: undefined, endDate: undefined };
        }
    };

    const fetchAnalytics = async () => {
        try {
            const tzOffsetMinutes = new Date().getTimezoneOffset();
            const res = await adminFormsAPI.getFormsAnalytics({ tzOffsetMinutes });
            setAnalytics(res.data);
        } catch (e) {
            // optional
        }
    };

    const fetchForms = async (page = 1, currentLimit = limit) => {
        try {
            setLoading(true);
            const { startDate, endDate } = computeDateRange(filters.dateRange);
            const res = await emailsAPI.getEmails({
                page,
                limit: currentLimit,
                status: filters.status || undefined,
                search: filters.q || undefined,
                startDate,
                endDate,
            });
            setSubmissions(res.data.submissions || []);
            setPagination(res.data.pagination || { page, pages: 1, total: 0 });
        } catch (e) {
            toast.error('Failed to load forms');
        } finally {
            setLoading(false);
        }
    };

    const fetchWebsites = async (page = 1, currentLimit = webLimit) => {
        try {
            setWebsitesLoading(true);
            const tzOffsetMinutes = new Date().getTimezoneOffset();
            const res = await adminFormsAPI.getFormsByWebsite({ page, limit: currentLimit, tzOffsetMinutes, sort: 'today' });
            setWebsites(res.data.websites || []);
            setWebPagination(res.data.pagination || { currentPage: page, totalPages: 1, totalWebsites: 0 });
        } catch (e) {
            toast.error('Failed to load websites');
        } finally {
            setWebsitesLoading(false);
        }
    };

    useEffect(() => {
        fetchForms(1, limit);
        fetchAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (activeTab === 'byWebsite') {
            fetchWebsites(webPagination.currentPage || 1, webLimit);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleChangePage = (_e, newPage) => {
        fetchForms(newPage + 1, limit);
    };

    const handleChangeRowsPerPage = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setLimit(newLimit);
        fetchForms(1, newLimit);
    };

    const handleWebPageChange = (_e, newPage) => {
        fetchWebsites(newPage + 1, webLimit);
    };

    const handleWebRowsPerPage = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setWebLimit(newLimit);
        fetchWebsites(1, newLimit);
    };

    const applyFilters = () => {
        fetchForms(1, limit);
    };

    const clearFilters = () => {
        setFilters({ q: '', status: '', dateRange: '' });
        fetchForms(1, limit);
    };

    const totalsBy = useMemo(() => {
        const byWebsite = {};
        submissions.forEach((s) => {
            if (s.website) {
                const wKey = s.website._id || s.website; // populated or id
                byWebsite[wKey] = byWebsite[wKey] || { name: s.website.name, count: 0 };
                byWebsite[wKey].count += 1;
            }
        });
        return { byWebsite };
    }, [submissions]);

    return (
        <Box>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                        <CardHeader
                            title={
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="h5">Forms</Typography>
                                    <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)} aria-label="forms tabs">
                                        <Tab value="all" label="All Forms" />
                                        <Tab value="byWebsite" label="Forms By Website" />
                                    </Tabs>
                                </Stack>
                            }
                            action={
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title="Refresh">
                                        <IconButton onClick={() => { activeTab === 'all' ? fetchForms(pagination.page, limit) : fetchWebsites(webPagination.currentPage, webLimit); fetchAnalytics(); }}>
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            }
                        />
                        <CardContent>
                            {activeTab === 'all' ? (
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Search"
                                            placeholder="Name, Email, IP, Notes"
                                            value={filters.q}
                                            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                                            InputProps={{ endAdornment: <SearchIcon fontSize="small" /> }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            select
                                            label="Date Range"
                                            value={filters.dateRange}
                                            onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            <MenuItem value="today">Today</MenuItem>
                                            <MenuItem value="thisWeek">This Week</MenuItem>
                                            <MenuItem value="thisMonth">This Month</MenuItem>
                                            <MenuItem value="lastMonth">Last Month</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            select
                                            label="Status"
                                            value={filters.status}
                                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            {['pending', 'processed', 'spam', 'failed'].map(s => (
                                                <MenuItem key={s} value={s}>{s}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <Stack direction="row" spacing={1}>
                                            <Button variant="contained" onClick={applyFilters} startIcon={<FilterListIcon />}>Apply</Button>
                                            <Button variant="text" onClick={clearFilters}>Clear</Button>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            ) : (
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Websites sorted by highest Today forms.</Typography>
                                    </Grid>
                                </Grid>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Analytics quick stats */}
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        {[{ key: 'today', label: 'Today Forms', value: analytics?.totals?.today ?? 0 },
                        { key: 'thisWeek', label: 'This Week Forms', value: analytics?.totals?.week ?? 0 },
                        { key: 'thisMonth', label: 'This Month Forms', value: analytics?.totals?.month ?? 0 },
                        { key: 'lastMonth', label: 'Last Month Forms', value: analytics?.totals?.lastMonth ?? 0 }].map((item) => (
                            <Grid item xs={12} md={3} key={item.key}>
                                <Card sx={{ borderRadius: 3 }}>
                                    <CardContent>
                                        <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                                            <Typography variant="subtitle1" fontWeight={600}>{item.label}</Typography>
                                            <Button size="small" onClick={() => { setFilters(f => ({ ...f, dateRange: item.key })); fetchForms(1, limit); }}>Filter</Button>
                                        </Stack>
                                        <Typography variant="h3" fontWeight={800}> {item.value} </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                {/* Content tables */}
                {activeTab === 'all' ? (
                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: 3 }}>
                            <CardContent>
                                {loading ? (
                                    <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
                                ) : (
                                    <>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Submission</TableCell>
                                                    <TableCell>From</TableCell>
                                                    <TableCell>Website</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell align="center">Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {submissions.map((s) => (
                                                    <TableRow key={s._id} hover>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <Avatar sx={{ width: 28, height: 28, bgcolor: 'secondary.light' }}>{(s._id || 'S')?.slice(-2)}</Avatar>
                                                                <Box>
                                                                    <Typography variant="body2" fontWeight={600}>{s._id}</Typography>
                                                                    <Typography variant="caption" color="text.secondary">{s.submitterInfo?.ip || '-'}</Typography>
                                                                </Box>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight={600}>{s.formData?.name || '-'}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{s.formData?.email || '-'}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">{s.website?.name || '-'}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{s.website?.siteId || '-'}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={s.status} color={statusColor(s.status)} size="small" />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">{dayjs(s.createdAt).format('YYYY-MM-DD HH:mm')}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{dayjs(s.createdAt).fromNow()}</Typography>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Tooltip title="Open">
                                                                <IconButton size="small" onClick={() => window.open(`/dashboard/emails?focus=${s._id}`, '_blank')}>
                                                                    <LaunchIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {submissions.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={6} align="center">
                                                            <Typography variant="body2" color="text.secondary">No forms found</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                        <TablePagination
                                            component="div"
                                            count={pagination.total || 0}
                                            page={(pagination.page || 1) - 1}
                                            onPageChange={handleChangePage}
                                            rowsPerPage={limit}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            rowsPerPageOptions={[10, 25, 50, 100]}
                                        />
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ) : (
                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: 3 }}>
                            <CardContent>
                                {websitesLoading ? (
                                    <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
                                ) : (
                                    <>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Website</TableCell>
                                                    <TableCell>Site ID</TableCell>
                                                    <TableCell align="right">Today</TableCell>
                                                    <TableCell align="right">This Week</TableCell>
                                                    <TableCell align="right">This Month</TableCell>
                                                    <TableCell align="right">Last Month</TableCell>
                                                    <TableCell align="right">Total</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {websites.map((w) => (
                                                    <TableRow key={w.websiteId} hover>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight={600}>{w.name}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary">{w.siteId}</Typography>
                                                        </TableCell>
                                                        <TableCell align="right">{w.today}</TableCell>
                                                        <TableCell align="right">{w.week}</TableCell>
                                                        <TableCell align="right">{w.month}</TableCell>
                                                        <TableCell align="right">{w.lastMonth}</TableCell>
                                                        <TableCell align="right">{w.total}</TableCell>
                                                    </TableRow>
                                                ))}
                                                {websites.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={7} align="center">
                                                            <Typography variant="body2" color="text.secondary">No websites found</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                        <TablePagination
                                            component="div"
                                            count={webPagination.totalWebsites || 0}
                                            page={(webPagination.currentPage || 1) - 1}
                                            onPageChange={handleWebPageChange}
                                            rowsPerPage={webLimit}
                                            onRowsPerPageChange={handleWebRowsPerPage}
                                            rowsPerPageOptions={[10, 25, 50, 100]}
                                        />
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Aggregations by website (current page) */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 3 }}>
                        <CardHeader title={<Typography variant="h6">By Website (current page)</Typography>} />
                        <CardContent>
                            {Object.keys(totalsBy.byWebsite).length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No data</Typography>
                            ) : (
                                Object.values(totalsBy.byWebsite).map((w) => (
                                    <Box key={w.name} display="flex" justifyContent="space-between" py={1}>
                                        <Typography>{w.name}</Typography>
                                        <Typography fontWeight={600}>{w.count}</Typography>
                                    </Box>
                                ))
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Global top websites/users from analytics */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 3 }}>
                        <CardHeader title={<Typography variant="h6">Top Websites (all)</Typography>} />
                        <CardContent>
                            {!analytics?.byWebsite?.length ? (
                                <Typography variant="body2" color="text.secondary">No data</Typography>
                            ) : (
                                analytics.byWebsite.map((w) => (
                                    <Box key={w.websiteId} display="flex" justifyContent="space-between" py={1}>
                                        <Typography>{w.name}</Typography>
                                        <Typography fontWeight={600}>{w.count}</Typography>
                                    </Box>
                                ))
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 3 }}>
                        <CardHeader title={<Typography variant="h6">Top Users (all)</Typography>} />
                        <CardContent>
                            {!analytics?.byUser?.length ? (
                                <Typography variant="body2" color="text.secondary">No data</Typography>
                            ) : (
                                analytics.byUser.map((u) => (
                                    <Box key={u._id} display="flex" justifyContent="space-between" py={1}>
                                        <Typography>User {u._id}</Typography>
                                        <Typography fontWeight={600}>{u.count}</Typography>
                                    </Box>
                                ))
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SuperAdminFormsPage;


